import React, { useState, useRef, useEffect } from 'react';

const TwoFactorPrompt = ({ onSubmit, onCancel, error: externalError, loading }) => {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [internalError, setInternalError] = useState('');
  const inputRefs = useRef([]);

  const error = externalError || internalError;

  useEffect(() => {
    // Auto-focus first input on mount
    if (!useRecovery && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [useRecovery]);

  const handleDigitChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    setInternalError('');

    // Auto-advance to next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (digit && index === 5) {
      const code = newDigits.join('');
      if (code.length === 6) {
        onSubmit(code, null);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pasted[i] || '';
      }
      setDigits(newDigits);

      // Focus last filled or next empty
      const focusIdx = Math.min(pasted.length, 5);
      inputRefs.current[focusIdx]?.focus();

      // Auto-submit if complete
      if (pasted.length === 6) {
        onSubmit(pasted, null);
      }
    }
  };

  const handleRecoverySubmit = (e) => {
    e.preventDefault();
    if (!recoveryCode.trim()) {
      setInternalError('Введите recovery код');
      return;
    }
    onSubmit(null, recoveryCode.trim());
  };

  const handleTotpSubmit = (e) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length !== 6) {
      setInternalError('Введите 6-значный код');
      return;
    }
    onSubmit(code, null);
  };

  return (
    <div style={styles.container}>
      <div style={styles.icon}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
      </div>

      <h2 style={styles.title}>Two-Factor Authentication</h2>
      <p style={styles.subtitle}>
        {useRecovery
          ? 'Enter one of your recovery codes'
          : 'Enter the 6-digit code from your authenticator app'
        }
      </p>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {!useRecovery ? (
        <form onSubmit={handleTotpSubmit}>
          <div style={styles.codeContainer} onPaste={handlePaste}>
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleDigitChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                style={{
                  ...styles.digitInput,
                  borderColor: digit ? '#6366f1' : 'rgba(255,255,255,0.1)',
                }}
                disabled={loading}
                autoComplete="one-time-code"
              />
            ))}
          </div>

          <button type="submit" style={{ ...styles.submitBtn, opacity: loading ? 0.6 : 1 }} disabled={loading}>
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleRecoverySubmit}>
          <input
            type="text"
            value={recoveryCode}
            onChange={e => { setRecoveryCode(e.target.value.toUpperCase()); setInternalError(''); }}
            placeholder="XXXXXXXX"
            style={styles.recoveryInput}
            disabled={loading}
            autoFocus
            autoComplete="off"
          />

          <button type="submit" style={{ ...styles.submitBtn, opacity: loading ? 0.6 : 1 }} disabled={loading}>
            {loading ? 'Verifying...' : 'Use Recovery Code'}
          </button>
        </form>
      )}

      <button
        onClick={() => { setUseRecovery(!useRecovery); setInternalError(''); setRecoveryCode(''); setDigits(['','','','','','']); }}
        style={styles.toggleBtn}
        disabled={loading}
      >
        {useRecovery ? 'Use authenticator app instead' : 'Lost access? Use recovery code'}
      </button>

      {onCancel && (
        <button onClick={onCancel} style={styles.cancelBtn} disabled={loading}>
          Back to login
        </button>
      )}
    </div>
  );
};

const styles = {
  container: {
    textAlign: 'center',
    padding: '0',
  },
  icon: {
    marginBottom: '20px',
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: '600',
    color: '#eef2ff',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#6b7a9f',
    fontSize: '0.9rem',
    margin: '0 0 24px 0',
    lineHeight: '1.5',
  },
  error: {
    padding: '10px 16px',
    background: 'rgba(255,71,87,0.1)',
    border: '1px solid rgba(255,71,87,0.3)',
    borderRadius: '8px',
    color: '#ff4757',
    fontSize: '0.85rem',
    marginBottom: '20px',
  },
  codeContainer: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  digitInput: {
    width: '48px',
    height: '56px',
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: '600',
    fontFamily: 'JetBrains Mono, monospace',
    background: '#0f1422',
    border: '2px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    color: '#eef2ff',
    outline: 'none',
    transition: 'border-color 0.2s',
    caretColor: '#6366f1',
  },
  recoveryInput: {
    width: '100%',
    padding: '16px 20px',
    textAlign: 'center',
    fontSize: '1.2rem',
    fontWeight: '600',
    fontFamily: 'JetBrains Mono, monospace',
    background: '#0f1422',
    border: '2px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    color: '#eef2ff',
    outline: 'none',
    marginBottom: '24px',
    boxSizing: 'border-box',
    letterSpacing: '3px',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '16px',
    transition: 'opacity 0.2s',
  },
  toggleBtn: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    fontSize: '0.85rem',
    cursor: 'pointer',
    padding: '8px',
    display: 'block',
    width: '100%',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
  cancelBtn: {
    background: 'none',
    border: 'none',
    color: '#6b7a9f',
    fontSize: '0.85rem',
    cursor: 'pointer',
    padding: '8px',
    marginTop: '8px',
    display: 'block',
    width: '100%',
  },
};

export default TwoFactorPrompt;
